import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import logoImage from '@/assets/logo.webp';

interface Banner {
  id: string;
  imageUrl: string;
  alt: string;
  linkUrl?: string;
  sortOrder: number;
}

interface AppSettings {
  appName: string;
  logoUrl: string;
  banners: Banner[];
}

const defaultSettings: AppSettings = {
  appName: 'قسيمة',
  logoUrl: logoImage,
  banners: [],
};

function isLegacyLogoUrl(url: string): boolean {
  const normalized = url.trim().toLowerCase();
  if (!normalized) return false;
  return (
    normalized.includes('i.ibb.co/') &&
    (normalized.includes('generated-image-december-20-2025-8-48-am') ||
      normalized.includes('photoroom'))
  );
}

function normalizeSettings(data: Partial<AppSettings> | undefined): AppSettings {
  const appName =
    typeof data?.appName === 'string' && data.appName.trim()
      ? data.appName.trim()
      : defaultSettings.appName;
  const rawLogo =
    typeof data?.logoUrl === 'string' ? data.logoUrl.trim() : '';
  const logoUrl =
    !rawLogo || isLegacyLogoUrl(rawLogo) ? defaultSettings.logoUrl : rawLogo;
  const banners = Array.isArray(data?.banners) ? data.banners : [];
  return { appName, logoUrl, banners };
}

export function useAppSettings() {
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const docRef = doc(db, 'settings', 'app');
    
    const unsubscribe = onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<AppSettings>;
          const normalized = normalizeSettings(data);
          console.log('📱 App Settings loaded:', normalized);
          setSettings(normalized);
        } else {
          console.log('📱 No settings found, using defaults');
        }
        setLoading(false);
      },
      (err) => {
        // Silently handle permission errors - fallback to defaults
        console.error('📱 Settings load error:', err.message);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return { settings, loading, error };
}

export type { AppSettings, Banner };
